// Import d'un export de pointeuse (ZKTeco ou autre) : CSV / TXT / DAT.
import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { rhAPI } from '../../services/api';
import { apiError } from '../../utils/format';

const DATE_RE = /(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})|(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/;
const TIME_RE = /(\d{1,2}):(\d{2})/;

function splitLine(line, sep) {
  const out = []; let cur = ''; let q = false;
  for (const ch of line) {
    if (ch === '"') q = !q;
    else if (ch === sep && !q) { out.push(cur.trim()); cur = ''; } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

export function parseFile(text) {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return null;
  const counts = ['\t', ';', ','].map((s) => [s, lines.slice(0, 5).reduce((n, l) => n + l.split(s).length, 0)]);
  const sep = counts.sort((a, b) => b[1] - a[1])[0][0];
  const rows = lines.map((l) => splitLine(l, sep));
  const hasHeader = !rows[0].some((c) => DATE_RE.test(c));
  const header = hasHeader ? rows[0] : rows[0].map((_, i) => `Colonne ${i + 1}`);
  const data = hasHeader ? rows.slice(1) : rows;
  // Devinettes : colonne date (avec ou sans heure), colonne heure séparée, colonne badge
  const sample = data.slice(0, 20);
  const isDate = (i) => sample.every((r) => DATE_RE.test(r[i] || ''));
  const dateCol = header.findIndex((_, i) => isDate(i));
  const hasTimeInDate = dateCol >= 0 && sample.every((r) => TIME_RE.test((r[dateCol] || '').replace(DATE_RE, '')));
  const timeCol = hasTimeInDate ? -1 : header.findIndex((_, i) => i !== dateCol && sample.every((r) => /^\d{1,2}:\d{2}/.test(r[i] || '')));
  const byName = header.findIndex((h) => /(ac-?no|no\.|badge|matricule|user ?id|^id$|n°|numéro|enroll)/i.test(h));
  const badgeCol = byName >= 0 && byName !== dateCol ? byName
    : header.findIndex((_, i) => i !== dateCol && i !== timeCol && sample.every((r) => /^[A-Za-z0-9-]{1,15}$/.test(r[i] || '')));
  return { header, data, dateCol, timeCol, badgeCol: badgeCol < 0 ? 0 : badgeCol };
}

function toIso(dateStr, timeStr) {
  const d = DATE_RE.exec(dateStr || '');
  const t = TIME_RE.exec(timeStr ?? (dateStr || '').replace(DATE_RE, ''));
  if (!d || !t) return null;
  const [y, m, j] = d[1] ? [d[1], d[2], d[3]] : [d[6], d[5], d[4]];   // AAAA-MM-JJ ou JJ/MM/AAAA
  return `${y}-${m.padStart(2, '0')}-${j.padStart(2, '0')} ${t[1].padStart(2, '0')}:${t[2]}`;
}

const ImportPointeuseDialog = ({ open, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [cols, setCols] = useState({ badgeCol: 0, dateCol: 0, timeCol: -1 });
  const [ecraser, setEcraser] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setError(''); setResult(null); setEcraser(false); };
  const close = () => { reset(); onClose(); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    reset();
    if (f.size > 10 * 1024 * 1024) { setError('Fichier trop volumineux (10 Mo maximum)'); return; }
    const parsed = parseFile(await f.text());
    if (!parsed || !parsed.data.length) { setError('Fichier vide ou illisible'); return; }
    if (parsed.dateCol < 0) setError("Aucune colonne de date reconnue. Exportez le relevé en CSV ou TXT depuis le logiciel de la pointeuse.");
    setFile({ name: f.name, ...parsed });
    setCols({ badgeCol: parsed.badgeCol, dateCol: Math.max(0, parsed.dateCol), timeCol: parsed.timeCol });
  };

  const lignes = useMemo(() => (file ? file.data.map((r) => ({
    badge: r[cols.badgeCol],
    horodatage: toIso(r[cols.dateCol], cols.timeCol >= 0 ? r[cols.timeCol] : undefined),
  })) : []), [file, cols]);
  const valides = lignes.filter((l) => l.horodatage && l.badge);

  const send = async () => {
    setBusy(true); setError('');
    try {
      const r = await rhAPI.importPointages({ lignes: valides, ecraser });
      setResult(r.data);
      onDone?.();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  const colSelect = (label, key, allowNone = false) => (
    <TextField select size="small" label={label} value={cols[key]} sx={{ minWidth: 170 }}
      onChange={(e) => setCols({ ...cols, [key]: Number(e.target.value) })}>
      {allowNone && <MenuItem value={-1}>(dans la colonne date)</MenuItem>}
      {file.header.map((h, i) => <MenuItem key={i} value={i}>{h || `Colonne ${i + 1}`}</MenuItem>)}
    </TextField>
  );

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>Importer les pointages de la pointeuse</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Exportez le relevé des badgeages (CSV, TXT ou fichier .dat de la pointeuse). Pour chaque employé et chaque jour,
          le premier badgeage devient l'heure d'arrivée et le dernier l'heure de départ. Les employés sont reconnus par leur
          n° de badge (fiche employé) ou, à défaut, leur matricule.
        </Typography>
        <Button variant="outlined" component="label">
          Choisir le fichier
          <input hidden type="file" accept=".csv,.txt,.dat,text/csv,text/plain" onChange={onFile} />
        </Button>
        {file && <Typography component="span" sx={{ ml: 2 }}>{file.name} — {file.data.length} lignes</Typography>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {file && !result && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              {colSelect('Colonne badge / matricule', 'badgeCol')}
              {colSelect('Colonne date (et heure)', 'dateCol')}
              {colSelect('Colonne heure', 'timeCol', true)}
            </Box>
            <Table size="small" sx={{ mt: 2 }}>
              <TableHead><TableRow><TableCell>Badge</TableCell><TableCell>Date et heure lues</TableCell></TableRow></TableHead>
              <TableBody>
                {lignes.slice(0, 6).map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>{l.badge}</TableCell>
                    <TableCell sx={{ color: l.horodatage ? undefined : 'error.main' }}>{l.horodatage || 'illisible'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="body2" sx={{ mt: 1 }}>{valides.length} badgeages lisibles sur {lignes.length}.</Typography>
            <FormControlLabel sx={{ mt: 1 }} control={<Checkbox checked={ecraser} onChange={(e) => setEcraser(e.target.checked)} />}
              label="Remplacer les pointages déjà saisis pour les mêmes jours" />
          </>
        )}

        {result && (
          <Alert severity={result.badges_inconnus.length ? 'warning' : 'success'} sx={{ mt: 2 }}>
            {result.crees} journées créées, {result.mis_a_jour} mises à jour, {result.ignores} déjà saisies (conservées)
            {result.invalides ? `, ${result.invalides} lignes illisibles` : ''}.
            {result.badges_inconnus.length > 0 && (
              <> Badges inconnus : {result.badges_inconnus.join(', ')} — renseignez-les dans les fiches employés puis relancez l'import.</>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{result ? 'Fermer' : 'Annuler'}</Button>
        {!result && <Button variant="contained" disabled={busy || !valides.length} onClick={send}>Importer {valides.length || ''} badgeages</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default ImportPointeuseDialog;
